import _ from 'lodash';
import React from 'react';
import PropTypes from 'prop-types';
import toastr from 'toastr';
import { ThemeProvider } from 'styled-components';
import { useChangeAwareEffect } from 'use-change-aware-effect';
import commonProps from './contributor-profile-editor-components/common-props';
import parseFailXhr from '../../../shared/parse-fail-xhr';
import { defaultProfileCompleteness, profileCompletenessType } from './headers-components/score';
import HeaderWrapper from './headers-components/wrapper';
import ErrorBoundary from '../../../shared/error-boundary';
import Loading from '../../../shared/loading';
import About from './contributor-profile-editor-components/about';
import Expertise from './contributor-profile-editor-components/expertise';
import Disclaimer from './contributor-profile-editor-components/disclaimer';
import Career from './contributor-profile-editor-components/career';
import InvestmentPhilosophy from './contributor-profile-editor-components/investment-philosophy';
import HeadlineGrab from './contributor-profile-editor-components/headline-grab';
import FeaturedWires from './contributor-profile-editor-components/featured-wires';
import SocialLinks from './contributor-profile-editor-components/social-links';
import { useGetContributorProfile } from '../../../services/profiles/contributor-settings-service';

const desc = 'As a contributor on Livewire you can provide your readers with'
  + ' information explaining your investment experience and expertise.'
  + ' Completing the information below can help improve your readership and'
  + ' awareness on Livewire.';

const fallbackProfile = {
  accred: '',
  areasOfExpertise: [],
  blurb: '',
  city: null,
  country: null,
  disclaimer: '',
  headlineGrab: '',
  investmentPhilosophy: [],
  activeFeatures: [],
  joinedCurrentRoleAt: null,
  joinedIndustryAt: null,
  organisation: '',
  position: '',
  recentCareer: null,
  linkedinUrl: null,
  twitterUrl: null,
};

const keyGetter = (v, k) => _.camelCase(k);

const VAL_CONVERTER = {
  recentCareer: v => (v ? _.mapKeys(v, keyGetter) : null),
  areasOfExpertise: v => (v ? _.map(v, a => ({ ...a, assignedToMe: a.assigned_to_me })) : []),
};
const fallback = (v, k) => v || fallbackProfile[k];
const valueGetter = (v, k) => (VAL_CONVERTER[k] ? VAL_CONVERTER[k](v) : fallback(v, k));

export default function ContributorProfileEditor({ profileCompleteness, newNavEnabled }) {
  const [profile, setProfile] = React.useState(fallbackProfile);
  const [{ data, loading, error }, get] = useGetContributorProfile();
  const loadingRef = React.useRef();
  const headerRef = React.useRef();

  useChangeAwareEffect(({ isMount }) => {
    isMount && get();
  }, {});

  React.useEffect(() => loadingRef.current.toggle(loading), [loading]);

  React.useEffect(() => {
    data && setProfile(_(data).mapKeys(keyGetter).mapValues(valueGetter).value());
  }, [data]);

  React.useEffect(() => {
    if (error) {
      const errors = parseFailXhr(error);
      errors.anyUnhandledErrors() && toastr.error(errors.getErrorSummary(), 'Error loading contributor profile');
    }
  }, [error]);

  const {
    activeFeatures, organisation, position,
  } = profile;

  const isStd = _.includes(activeFeatures, 'standard_profile_page');
  const prmFeaturedWires = _.includes(activeFeatures, 'premium_featured_wires');
  const prmSocialLinks = _.includes(activeFeatures, 'premium_social_links');

  const stdProps = {
    ...profile,
    onUpdate: d => headerRef && headerRef.current && headerRef.current.update(d),
  };

  return (
    <ErrorBoundary>
      <ThemeProvider theme={commonProps()}>
        <HeaderWrapper
          ref={headerRef}
          title="Contributor Profile"
          desc={desc}
          profileCompleteness={profileCompleteness}
          newNavEnabled={newNavEnabled}
          showProfileLink
        />
        <form className="profiles-me-contributor-profile --user-facing" action="#">
          <Loading ref={loadingRef}>
            {isStd && <HeadlineGrab {...stdProps} />}
            <About {...stdProps} />
            <Expertise {...stdProps} />
            {isStd && !!(organisation.length && position.length) && <Career {...stdProps} />}
            {isStd && <InvestmentPhilosophy {...stdProps} />}
            {isStd && prmFeaturedWires && <FeaturedWires {...stdProps} />}
            {isStd && prmSocialLinks && <SocialLinks {...stdProps} />}
            <Disclaimer {...profile} />
          </Loading>
        </form>
      </ThemeProvider>
    </ErrorBoundary>
  );
}

ContributorProfileEditor.propTypes = {
  profileCompleteness: profileCompletenessType,
  newNavEnabled: PropTypes.bool,
};
ContributorProfileEditor.defaultProps = {
  profileCompleteness: defaultProfileCompleteness,
  newNavEnabled: false,
};
